<template>
  <div class="today-page">
    <header class="today-header">
      <div>
        <p>Aujourd’hui</p>
        <h1>Vue d’ensemble</h1>
        <span
          >Retrouvez les échéances et les prochaines actions de votre
          collection.</span
        >
      </div>
      <button
        type="button"
        :disabled="loading"
        :aria-label="loading ? 'Actualisation…' : 'Actualiser'"
        @click="loadDashboardData"
      >
        <span :class="{ spinning: loading }">↻</span
        ><span class="refresh-label">{{
          loading ? "Actualisation…" : "Actualiser"
        }}</span>
      </button>
    </header>
    <div v-if="errorMessage" class="error-banner" role="alert">
      {{ errorMessage }}
    </div>
    <section class="kpis" aria-label="Indicateurs principaux">
      <router-link to="/offers"
        ><strong>{{ dashboard.offers_total || 0 }}</strong
        ><span>Annonces suivies</span
        ><small
          >{{ activeOffers.length }} active{{
            activeOffers.length > 1 ? "s" : ""
          }}
          chargée{{ activeOffers.length > 1 ? "s" : "" }}</small
        ></router-link
      >
      <router-link to="/myoffers"
        ><strong>{{ dashboard.offers_owned || 0 }}</strong
        ><span>Achats remportés</span
        ><small
          >{{ formatCurrency(dashboard.costs?.total || 0) }} investis</small
        ></router-link
      >
      <router-link to="/items"
        ><strong>{{ dashboard.items_owned || 0 }}</strong
        ><span>Objets en collection</span
        ><small
          >{{ dashboard.sold?.items || 0 }} vendu{{
            dashboard.sold?.items > 1 ? "s" : ""
          }}</small
        ></router-link
      >
      <router-link to="/sales"
        ><strong>{{ formatCurrency(dashboard.sold?.value || 0) }}</strong
        ><span>Ventes enregistrées</span
        ><small
          >{{ formatCurrency(dashboard.costs?.total || 0) }} de coût d’achat
          total</small
        ></router-link
      >
    </section>
    <div v-if="loading" class="loading-state">
      <LoaderDiv
        :isLoading="true"
        variant="modern"
        loaderText="Préparation de votre journée…"
      />
    </div>
    <template v-else>
      <section class="attention">
        <header>
          <div>
            <p>À traiter</p>
            <h2>Vos prochaines actions</h2>
          </div>
          <span>{{ actionCount }} élément{{ actionCount > 1 ? "s" : "" }}</span>
        </header>
        <div class="action-grid">
          <router-link to="/jobs" class="action-card urgent"
            ><span class="icon">◷</span>
            <div>
              <strong
                >{{ upcomingJobs.length }} échéance{{
                  upcomingJobs.length > 1 ? "s" : ""
                }}</strong
              >
              <p>
                {{
                  upcomingJobs.length
                    ? "Des rappels sont programmés prochainement."
                    : "Aucun rappel imminent."
                }}
              </p>
            </div>
            <b>Voir →</b></router-link
          >
          <router-link to="/myoffers" class="action-card"
            ><span class="icon">□</span>
            <div>
              <strong
                >{{ incompleteAcquisitions.length }} achat{{
                  incompleteAcquisitions.length > 1 ? "s" : ""
                }}
                à compléter</strong
              >
              <p>
                {{
                  incompleteAcquisitions.length
                    ? "Ces achats ne contiennent encore aucun objet."
                    : "Les achats chargés possèdent un contenu."
                }}
              </p>
            </div>
            <b>Ouvrir →</b></router-link
          >
          <router-link to="/sales" class="action-card"
            ><span class="icon">◇</span>
            <div>
              <strong
                >{{ listedSales.length }} vente{{
                  listedSales.length > 1 ? "s" : ""
                }}
                en ligne</strong
              >
              <p>
                {{
                  listedSales.length
                    ? "Gardez un œil sur vos annonces actives."
                    : "Aucune vente en ligne actuellement."
                }}
              </p>
            </div>
            <b>Suivre →</b></router-link
          >
        </div>
      </section>
      <div class="today-grid">
        <section class="panel deadlines">
          <header>
            <div>
              <p>Échéances</p>
              <h2>Annonces à surveiller</h2>
            </div>
            <router-link to="/offers">Toutes les annonces →</router-link>
          </header>
          <div v-if="activeOffers.length" class="rows">
            <EntityLink
              v-for="offer in activeOffers.slice(0, 5)"
              :key="offer._key"
              type="offer"
              :id="offer._key"
              ><span class="thumb">{{
                (offer.title || "A").charAt(0).toUpperCase()
              }}</span>
              <div>
                <strong>{{ offer.title || "Annonce sans titre" }}</strong
                ><small
                  >#{{ offer._key }} · {{ formatDate(offer.endtime) }}</small
                >
              </div>
              <b :class="timeClass(offer.endtime)">{{
                relativeTime(offer.endtime)
              }}</b></EntityLink
            >
          </div>
          <div v-else class="empty">
            Aucune annonce active dans les données chargées.
          </div>
        </section>
        <section class="panel performance">
          <header>
            <div>
              <p>Performance</p>
              <h2>Collection et revente</h2>
            </div>
          </header>
          <div class="profit">
            <span>Résultat estimé</span
            ><strong :class="calculatedProfit >= 0 ? 'positive' : 'negative'">{{
              formatCurrency(calculatedProfit)
            }}</strong
            ><small>{{ profitabilityRate }} % du capital investi</small>
          </div>
          <dl>
            <div>
              <dt>Objets vendus</dt>
              <dd>{{ dashboard.sold?.items || 0 }}</dd>
            </div>
            <div>
              <dt>Achats liés</dt>
              <dd>{{ dashboard.sold?.offers_linked || 0 }}</dd>
            </div>
            <div>
              <dt>Marge confirmée</dt>
              <dd>
                {{ formatCurrency(dashboard.sold?.confirmed_margin || 0) }}
              </dd>
            </div>
          </dl>
        </section>
      </div>
      <section class="recent">
        <header>
          <div>
            <p>Activité</p>
            <h2>Achats récents à organiser</h2>
          </div>
          <router-link to="/myoffers">Tous les achats →</router-link>
        </header>
        <div v-if="recentAcquisitions.length" class="recent-table">
          <table>
            <thead>
              <tr>
                <th>Achat</th>
                <th>Coût</th>
                <th>Contenu</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="offer in recentAcquisitions.slice(0, 5)"
                :key="offer._key"
                class="clickable"
                @click="openDetail('purchase', offer._key)"
              >
                <td>
                  <EntityLink type="purchase" :id="offer._key"
                    ><strong>{{ offer.title || "Achat sans titre" }}</strong
                    ><small>#{{ offer._key }}</small></EntityLink
                  >
                </td>
                <td>
                  {{
                    formatCurrency(
                      offer.cost || offer.transactionCosts?.totalCost || 0,
                    )
                  }}
                </td>
                <td>
                  {{ offer.items?.count || 0 }} objet{{
                    Number(offer.items?.count) > 1 ? "s" : ""
                  }}
                </td>
                <td>{{ formatDate(offer.endtime) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty">Aucun achat récent disponible.</div>
      </section>
    </template>
  </div>
</template>
<script>
import { computed, onActivated, onMounted, ref } from "vue";
import axios from "axios";
import { useRouter } from "vue-router";
import { isTokenExpired } from "../auth";
import config from "@/config/config.js";
import LoaderDiv from "./LoaderDiv.vue";
import EntityLink from "./common/EntityLink.vue";
import { openDetail } from "../detailStack";
import {
  formatDate as formatDateText,
  formatRemaining,
  hoursLeft,
  parseTime,
  useNow,
} from "../clock";
export default {
  name: "DashboardMain",
  components: { EntityLink, LoaderDiv },
  setup() {
    const now = useNow(1000);
    const router = useRouter(),
      loading = ref(true),
      errorMessage = ref(""),
      dashboard = ref({
        offers_total: 0,
        offers_owned: 0,
        items_owned: 0,
        costs: { total: 0 },
        sold: {
          items: 0,
          offers_linked: 0,
          value: 0,
          offers_linked_value: 0,
          confirmed_margin: 0,
        },
      }),
      activeOffers = ref([]),
      upcomingJobs = ref([]),
      recentAcquisitions = ref([]),
      listedSales = ref([]);
    const calculatedProfit = computed(
        () =>
          (Number(dashboard.value.sold?.value) || 0) -
          (Number(dashboard.value.sold?.offers_linked_value) || 0),
      ),
      profitabilityRate = computed(() => {
        const cost = Number(dashboard.value.costs?.total) || 0;
        return cost ? Math.round((calculatedProfit.value / cost) * 100) : 0;
      }),
      incompleteAcquisitions = computed(() =>
        recentAcquisitions.value.filter(
          (offer) => !(Number(offer.items?.count) > 0),
        ),
      ),
      actionCount = computed(
        () =>
          upcomingJobs.value.length +
          incompleteAcquisitions.value.length +
          listedSales.value.length,
      );
    const headers = () => ({
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      }),
      unwrap = (response) =>
        Array.isArray(response?.data?.result)
          ? response.data.result
          : Array.isArray(response?.data)
            ? response.data
            : [];
    // Kept alive between pages: reloaded on return when shown for a while
    // (a sale entered meanwhile…); quick round trips reuse what is shown
    const RELOAD_AFTER_MS = 10 * 60 * 1000;
    let loadedAt = 0;
    const loadDashboardData = async () => {
      const token = localStorage.getItem("access_token");
      if (isTokenExpired(token)) {
        router.push("/");
        return;
      }
      loading.value = true;
      errorMessage.value = "";
      const requests = await Promise.allSettled([
        axios.get(`${config.apiUrl}/dashboards`, { headers: headers() }),
        axios.get(`${config.apiUrl}/offers`, {
          params: { page: 0, limit: 8, lifecycle: "active" },
          headers: headers(),
        }),
        axios.get(`${config.apiUrl}/jobs`, { headers: headers() }),
        axios.get(`${config.apiUrl}/offers/owned`, {
          params: { page: 0, limit: 8 },
          headers: headers(),
        }),
        axios.get(`${config.apiUrl}/sales`, {
          params: { status: "listed", limit: 8 },
          headers: headers(),
        }),
      ]);
      if (requests[0].status === "fulfilled")
        dashboard.value = { ...dashboard.value, ...requests[0].value.data };
      else
        errorMessage.value =
          "Certains indicateurs ne sont pas disponibles pour le moment.";
      activeOffers.value =
        requests[1].status === "fulfilled"
          ? unwrap(requests[1].value)
              .filter((offer) => parseTime(offer.endtime) > Date.now())
              .sort((a, b) => parseTime(a.endtime) - parseTime(b.endtime))
          : [];
      upcomingJobs.value =
        requests[2].status === "fulfilled"
          ? unwrap(requests[2].value)
              .filter((job) => parseTime(job.notify_time) >= Date.now())
              .slice(0, 8)
          : [];
      recentAcquisitions.value =
        requests[3].status === "fulfilled" ? unwrap(requests[3].value) : [];
      listedSales.value =
        requests[4].status === "fulfilled" ? unwrap(requests[4].value) : [];
      loading.value = false;
      loadedAt = Date.now();
    };
    const formatCurrency = (value) =>
        new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR",
        }).format(Number(value) || 0),
      formatDate = (value) =>
        formatDateText(
          value,
          { dateStyle: "medium", timeStyle: "short" },
          "Date inconnue",
        ),
      // Remaining times count down on their own (shared clock)
      relativeTime = (value) => formatRemaining(value, now.value),
      timeClass = (value) => {
        const hours = hoursLeft(value, now.value);
        return hours <= 1 ? "critical" : hours <= 24 ? "warning" : "";
      };
    onMounted(loadDashboardData);
    onActivated(() => {
      if (!loading.value && Date.now() - loadedAt > RELOAD_AFTER_MS)
        loadDashboardData();
    });
    return {
      openDetail,
      actionCount,
      activeOffers,
      calculatedProfit,
      dashboard,
      errorMessage,
      formatCurrency,
      formatDate,
      incompleteAcquisitions,
      listedSales,
      loading,
      loadDashboardData,
      profitabilityRate,
      recentAcquisitions,
      relativeTime,
      timeClass,
      upcomingJobs,
    };
  },
};
</script>
<style scoped>
.today-page {
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  --green: #168a64;
  min-height: 100%;
  padding: var(--page-gutter);
  background: #f5f7fa;
  color: var(--text);
  font-family: "DM Sans", sans-serif;
}
.today-header,
.kpis,
.attention,
.today-grid,
.recent,
.error-banner,
.loading-state {
  width: 100%;
  min-width: 0;
  max-width: var(--content-width);
  margin-inline: auto;
}
.today-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
}
.today-header p,
.attention header p,
.panel header p,
.recent header p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.today-header h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--page-title-size);
  letter-spacing: -0.035em;
}
.today-header > div > span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}
.today-header button {
  display: flex;
  height: 40px;
  align-items: center;
  gap: 7px;
  padding: 0 14px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #566276;
  font-size: 11px;
  font-weight: 800;
}
.spinning {
  animation: spin 0.7s linear infinite;
}
.error-banner {
  margin-bottom: 14px;
  padding: 12px 15px;
  border: 1px solid #eccfcb;
  border-radius: 8px;
  background: #fff5f4;
  color: #a4453f;
  font-size: 12px;
}
.kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: 18px;
  border: 1px solid var(--line);
  border-radius: 11px;
  background: #fff;
}
.kpis a {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 18px 20px;
  border-right: 1px solid var(--line);
  color: inherit;
  text-decoration: none;
}
.kpis a:last-child {
  border: 0;
}
.kpis strong {
  font-size: 21px;
}
.kpis span {
  margin-top: 5px;
  font-size: 12px;
  font-weight: 700;
}
.kpis small {
  margin-top: 4px;
  color: var(--muted);
  font-size: var(--meta-size);
}
.positive {
  color: #168a64 !important;
}
.negative {
  color: #ba4943 !important;
}
.loading-state {
  min-height: 420px;
}
.attention {
  margin-bottom: 18px;
}
.attention > header,
.panel > header,
.recent > header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 12px;
}
.attention h2,
.panel h2,
.recent h2 {
  margin: 0;
  font-size: 17px;
}
.attention > header > span {
  padding: 5px 8px;
  border-radius: 12px;
  background: #eaf7f2;
  color: #168a64;
  font-size: var(--meta-size);
  font-weight: 800;
}
.action-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.action-card {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  color: inherit;
  text-decoration: none;
}
.action-card .icon {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  place-items: center;
  border-radius: 8px;
  background: #eaf7f2;
  color: var(--green);
  font-size: 18px;
}
.action-card.urgent .icon {
  background: #fff3e5;
  color: #ae6818;
}
.action-card div {
  min-width: 0;
  flex: 1;
}
.action-card strong {
  font-size: 12px;
}
.action-card p {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: var(--meta-size);
  line-height: 1.4;
}
.action-card b {
  color: #607086;
  font-size: var(--meta-size);
}
.today-grid {
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: minmax(0, 1.6fr) minmax(300px, 0.8fr);
  gap: 16px;
  margin-bottom: 18px;
}
.panel,
.recent {
  min-width: 0;
  overflow: hidden;
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 11px;
  background: #fff;
}
.panel header a,
.recent header a {
  color: #687487;
  font-size: var(--meta-size);
  font-weight: 700;
  text-decoration: none;
}
.rows {
  margin: 0 -18px -18px;
}
.rows a {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 11px;
  padding: 11px 18px;
  border-top: 1px solid #edf0f4;
  color: inherit;
  text-decoration: none;
}
.thumb {
  display: grid;
  width: 35px;
  height: 35px;
  flex: 0 0 35px;
  place-items: center;
  border-radius: 7px;
  background: #eef2f6;
  color: #617084;
  font-weight: 800;
}
.rows div {
  min-width: 0;
  flex: 1;
}
.rows strong,
.rows small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rows strong {
  font-size: 11px;
}
.rows small {
  margin-top: 3px;
  color: var(--muted);
  font-size: var(--meta-size);
}
.rows b {
  flex: 0 0 auto;
  font-size: 11px;
}
.rows b.critical {
  color: #ba4943;
}
.rows b.warning {
  color: #aa6919;
}
.profit {
  display: flex;
  flex-direction: column;
  padding: 17px;
  border-radius: 9px;
  background: #f5f8fa;
}
.profit span,
.profit small {
  color: var(--muted);
  font-size: var(--meta-size);
}
.profit strong {
  margin: 5px 0;
  font-size: 25px;
}
.performance dl {
  display: grid;
  gap: 8px;
  margin: 12px 0 0;
}
.performance dl div {
  display: flex;
  justify-content: space-between;
  padding: 9px 0;
  border-bottom: 1px solid #edf0f4;
}
.performance dt {
  color: var(--muted);
  font-size: var(--meta-size);
}
.performance dd {
  margin: 0;
  font-size: 11px;
  font-weight: 800;
}
.recent-table {
  width: calc(100% + 36px);
  max-width: calc(100% + 36px);
  margin: 0 -18px -18px;
  overflow-x: auto;
}
.recent table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}
.recent th:first-child {
  width: auto;
}
.recent th:nth-child(2) {
  width: 130px;
}
.recent th:nth-child(3) {
  width: 105px;
}
.recent th:nth-child(4) {
  width: 155px;
}
.recent th {
  padding: 10px 18px;
  border-block: 1px solid var(--line);
  background: #fff !important;
  color: #7d8796 !important;
  font-size: var(--meta-size);
  text-align: left;
  text-transform: uppercase;
}
.recent tr.clickable {
  cursor: pointer;
}
.recent tr.clickable:hover {
  background: #f7f9fb;
}
.recent .entity-link {
  display: block;
  color: inherit;
  text-decoration: none;
}
.recent td {
  padding: 11px 18px;
  border-bottom: 1px solid #edf0f4;
  font-size: 11px;
}
.recent tr:nth-child(even) {
  background: #fff;
}
.recent td:first-child {
  max-width: 500px;
}
.recent td strong,
.recent td small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.recent td small {
  margin-top: 3px;
  color: var(--muted);
  font-size: var(--meta-size);
}
.empty {
  padding: var(--page-gutter);
  color: var(--muted);
  font-size: 11px;
  text-align: center;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 1050px) {
  .kpis {
    grid-template-columns: 1fr 1fr;
  }
  .kpis a:nth-child(2) {
    border-right: 0;
  }
  .kpis a:nth-child(-n + 2) {
    border-bottom: 1px solid var(--line);
  }
  .action-grid {
    grid-template-columns: 1fr;
  }
  .today-grid {
    grid-template-columns: 1fr;
  }
  .panel,
  .recent {
    width: 100%;
    max-width: 100%;
  }
}
@media (max-width: 700px) {
  .today-page {
    padding: 20px 14px;
  }
  .today-header {
    align-items: flex-start;
    flex-direction: column;
  }
  .today-header h1 {
    font-size: 26px;
  }
  .kpis {
    grid-template-columns: 1fr 1fr;
  }
  .kpis a {
    padding: 14px;
  }
  .recent th:nth-child(3),
  .recent td:nth-child(3),
  .recent th:nth-child(4),
  .recent td:nth-child(4) {
    display: none;
  }
  .rows {
    width: calc(100% + 36px);
    max-width: calc(100% + 36px);
  }
  .rows a {
    padding-inline: 14px;
  }
  .rows strong,
  .rows small {
    max-width: 100%;
  }
  .recent th:nth-child(2) {
    width: 110px;
  }
}

@media (max-width: 460px) {
  /* Phone: the 4 figures in a compact 2 x 2 grid, so that the next blocks
     show on arrival; refresh button as an icon next to the title */
  .today-header {
    position: relative;
    padding-right: 52px;
  }
  .today-header button {
    position: absolute;
    top: 0;
    right: 0;
    width: 40px;
    padding: 0;
    justify-content: center;
  }
  .today-header .refresh-label {
    display: none;
  }
  .kpis {
    grid-template-columns: 1fr 1fr;
    margin-bottom: 14px;
  }
  .kpis a {
    padding: 11px 12px;
    border-right: 1px solid var(--line);
    border-bottom: 0;
  }
  .kpis a:nth-child(2n) {
    border-right: 0;
  }
  .kpis a:nth-child(-n + 2) {
    border-bottom: 1px solid var(--line);
  }
  .kpis strong {
    font-size: 18px;
  }
  .kpis span {
    margin-top: 3px;
    font-size: 11px;
  }
  .kpis small {
    margin-top: 2px;
  }
  .action-card b,
  .panel > header a,
  .recent > header a {
    display: none;
  }
  .recent th:nth-child(2),
  .recent td:nth-child(2) {
    display: none;
  }
}
</style>
